import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { History, ChevronLeft, Share2, Download, Copy, Check, Bell } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../lib/authStore';

import DualEngagementCurve from '../components/DualEngagementCurve';
import TribeV2BrainCinema from '../components/TribeV2BrainCinema';
import EngagementCurve from '../components/EngagementCurve';
import BrainVisualization from '../components/BrainVisualization';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 3000;

const GPU_STAGE_LABELS = {
  dependencies:     'Installing dependencies',
  video_ready:      'Video downloaded',
  model_loading:    'Loading fMRI model',
  model_loaded:     'Model ready',
  audio_processing: 'Processing audio',
  audio_done:       'Audio analysed',
  inference:        'Running neural inference',
  predictions_done: 'Predictions complete',
  brain_mapped:     'Mapping brain regions',
  uploading:        'Uploading results',
  completed:        'Complete',
  error:            'Failed',
};

// ── Shared colours ───────────────────────────────────────────
const T = {
  bg:         '#FFFFFF',
  bgSubtle:   '#FAFAFA',
  border:     '#E4E4E7',
  borderHover:'#D4D4D8',
  fg:         '#09090B',
  muted:      '#71717A',
  subtle:     '#A1A1AA',
  dim:        '#D4D4D8',
  green:      '#059669',
  red:        '#DC2626',
  amberB:     '#D97706',
};

// ── Nav ──────────────────────────────────────────────────────
function Nav({ navigate }) {
  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 24px',
      borderBottom: `1px solid ${T.border}`,
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }}>
      <button
        onClick={() => navigate('/')}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: 'none', border: 'none', cursor: 'pointer',
          color: T.muted, fontSize: 12,
          fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
          transition: 'color 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = T.fg)}
        onMouseLeave={e => (e.currentTarget.style.color = T.muted)}
      >
        <ChevronLeft size={13} /> NEW BATTLE
      </button>

      <a href="https://eonik.ai" style={{ display: 'flex', alignItems: 'center' }}>
        <img src="/assets/logo.svg" alt="eonik" style={{ height: 18, display: 'block' }} />
      </a>

      <button
        onClick={() => navigate('/history')}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '6px 12px',
          background: T.bgSubtle, border: `1px solid ${T.border}`,
          borderRadius: 100, cursor: 'pointer',
          color: T.muted, fontSize: 11,
          fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = T.fg; e.currentTarget.style.borderColor = T.borderHover; }}
        onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
      >
        <History size={11} /> HISTORY
      </button>
    </nav>
  );
}

// ── Spinner ──────────────────────────────────────────────────
function Spinner({ size = 11, color = T.muted }) {
  return (
    <div style={{
      width: size, height: size, flexShrink: 0,
      border: `1.5px solid ${T.border}`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'spin 0.9s linear infinite',
    }} />
  );
}

// ── Phase 1: Processing ──────────────────────────────────────
function JobColumn({ label, filename, job, tribe, events }) {
  const pipeStatus = job?.status || 'queued';
  const pipeDone   = pipeStatus === 'completed';
  const pipeError  = pipeStatus === 'error';
  const pipePct    = job?.progress || 0;

  const gpuStatus  = tribe?.status || 'not_submitted';
  const gpuPct     = tribe?.tribe_v2_progress_pct || 0;
  const gpuStage   = GPU_STAGE_LABELS[tribe?.tribe_v2_progress_stage] || tribe?.tribe_v2_progress_stage || '';
  const gpuDone    = gpuStatus === 'completed';
  const gpuError   = gpuStatus === 'error';
  const gpuActive  = gpuStatus !== 'not_submitted';

  const isB = label === 'B';

  // Derive header state
  const headerDone  = gpuDone;
  const headerError = pipeError || gpuError;

  const stepIcon = s => {
    if (s === 'completed') return <span style={{ color: T.green,   fontSize: 11 }}>✓</span>;
    if (s === 'error')     return <span style={{ color: T.red,     fontSize: 11 }}>✕</span>;
    if (s === 'pending')   return <span style={{ color: T.dim,     fontSize: 11 }}>○</span>;
    return <Spinner color={T.muted} />;
  };

  return (
    <div style={{
      flex: 1,
      background: T.bgSubtle,
      border: `1px solid ${headerDone
        ? (isB ? '#FDE68A' : T.borderHover)
        : headerError ? '#FECACA'
        : T.border}`,
      borderRadius: 16, padding: 24,
      transition: 'border-color 0.4s',
    }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{
            width: 24, height: 24, borderRadius: 6,
            background: headerDone
              ? (isB ? '#FEF3C7' : '#F0FDF4')
              : headerError ? '#FEF2F2' : T.bgSubtle,
            border: `1px solid ${headerDone
              ? (isB ? '#FDE68A' : '#BBF7D0')
              : headerError ? '#FECACA' : T.border}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
            color: headerDone
              ? (isB ? T.amberB : T.green)
              : headerError ? T.red : T.muted,
            transition: 'all 0.3s',
          }}>{label}</span>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 9,
            color: headerDone
              ? (isB ? T.amberB : T.green)
              : headerError ? T.red : T.dim,
            letterSpacing: '0.1em', transition: 'color 0.3s',
          }}>
            {headerDone ? 'COMPLETE' : headerError ? 'FAILED' : 'ANALYSING'}
          </span>
        </div>
        <p style={{
          fontSize: 12, color: T.subtle, margin: 0,
          fontFamily: 'var(--font-mono)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {filename?.split('/').pop() || filename || '—'}
        </p>
      </div>

      {/* Input video preview (as soon as S3 URL exists) */}
      {job?.video_s3_url && (
        <div style={{ marginBottom: 14 }}>
          <p style={{
            fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim,
            letterSpacing: '0.1em', marginBottom: 8,
          }}>
            INPUT VIDEO — {label}
          </p>
          <div style={{
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            overflow: 'hidden',
            background: '#000',
          }}>
            <video
              src={job.video_s3_url}
              controls
              playsInline
              preload="metadata"
              style={{ width: '100%', display: 'block', maxHeight: 210, objectFit: 'contain' }}
            />
          </div>
        </div>
      )}

      {/* ── Pipeline steps ── */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim, letterSpacing: '0.1em', marginBottom: 10 }}>
          PIPELINE
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(job?.steps || []).map(step => (
            <div key={step.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {stepIcon(step.status)}
              <span style={{
                fontSize: 11, fontFamily: 'var(--font-mono)',
                color: step.status === 'completed' ? T.muted
                     : step.status === 'error'     ? T.red
                     : step.status === 'pending'   ? T.dim
                     : T.fg,
              }}>
                {step.label}
              </span>
            </div>
          ))}

          {/* Show a placeholder row while steps haven't loaded yet */}
          {(!job?.steps || job.steps.length === 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner color={T.dim} />
              <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: T.dim }}>
                Initialising…
              </span>
            </div>
          )}
        </div>

        {/* Live current-step message */}
        {job?.current_step && !pipeDone && !pipeError && (
          <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: T.muted, margin: '8px 0 0' }}>
            ↳ {job.current_step}
          </p>
        )}

        {/* Pipeline thin progress bar */}
        {!pipeDone && !pipeError && pipePct > 0 && (
          <div style={{ height: 2, background: T.border, borderRadius: 1, marginTop: 10, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pipePct}%`,
              background: T.dim, borderRadius: 1,
              transition: 'width 0.5s ease',
            }} />
          </div>
        )}

        {/* Pipeline error message */}
        {pipeError && (
          <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: T.red, margin: '8px 0 0' }}>
            ✕ {job?.error || 'Pipeline failed'}
          </p>
        )}
      </div>

      {/* ── GPU · fMRI section ── */}
      {/* Show as soon as pipeline completes OR as soon as Kaggle has been submitted */}
      {(pipeDone || gpuActive) && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <p style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim, letterSpacing: '0.1em', margin: 0 }}>
              GPU · fMRI
            </p>
            <p style={{
              fontSize: 9, fontFamily: 'var(--font-mono)', margin: 0,
              color: gpuError ? T.red
                   : gpuDone  ? (isB ? T.amberB : T.green)
                   : T.muted,
            }}>
              {gpuError ? 'FAILED' : gpuDone ? '100%' : gpuActive ? `${gpuPct}%` : '—'}
            </p>
          </div>

          {/* Progress bar — hidden on error */}
          {!gpuError && (
            <div style={{ height: 3, background: T.border, borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
              <div style={{
                height: '100%',
                width: `${gpuDone ? 100 : gpuPct}%`,
                background: gpuDone ? (isB ? T.amberB : T.green) : T.dim,
                borderRadius: 2, transition: 'width 0.6s ease, background 0.3s',
              }} />
            </div>
          )}

          {/* Status text */}
          {gpuError && (
            <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: T.red, margin: 0 }}>
              {tribe?.error_msg
                ? tribe.error_msg.split('\n')[0].slice(0, 160)
                : (gpuStage || 'GPU job failed')}
            </p>
          )}
          {!gpuError && !gpuDone && gpuActive && gpuStage && (
            <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: T.muted, margin: 0 }}>
              {gpuStage}
            </p>
          )}
          {!gpuError && !gpuDone && !gpuActive && (
            <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: T.dim, margin: 0 }}>
              Queuing GPU job…
            </p>
          )}
          {!gpuError && gpuDone && (
            <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: isB ? T.amberB : T.green, margin: 0 }}>
              Neural data ready
            </p>
          )}
        </div>
      )}

      {/* When pipeline is still running, show GPU as pending */}
      {!pipeDone && !pipeError && !gpuActive && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <p style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim, letterSpacing: '0.1em', margin: 0 }}>
              GPU · fMRI
            </p>
            <p style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim, margin: 0 }}>
              pending
            </p>
          </div>
          <p style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: T.dim, margin: 0 }}>
            Waiting for pipeline…
          </p>
        </div>
      )}

      {/* Engine log — pipeline + GPU + events (cursor-like live thinking) */}
      <ThinkingFeed label={label} job={job} tribe={tribe} events={events} />
    </div>
  );
}

// ── Live thinking/log feed ────────────────────────────────────
function ThinkingFeed({ events, label, job, tribe }) {
  const feedRef = useRef(null);
  const synthetic = [];

  synthetic.push({
    type: 'pipeline',
    message: `Pipeline status: ${String(job?.status || 'queued').toUpperCase()}`,
  });

  if (job?.current_step) {
    synthetic.push({ type: 'pipeline', message: `Pipeline: ${job.current_step}` });
  }
  if (typeof job?.progress === 'number' && job.progress > 0 && job.status !== 'completed') {
    synthetic.push({ type: 'pipeline', message: `Pipeline progress ${job.progress}%` });
  }
  if (tribe?.status && tribe.status !== 'not_submitted') {
    const gpuStage = GPU_STAGE_LABELS[tribe?.tribe_v2_progress_stage] || tribe?.tribe_v2_progress_stage || tribe.status;
    synthetic.push({
      type: tribe.status === 'error' ? 'error' : 'gpu',
      message: `GPU: ${gpuStage}${tribe?.tribe_v2_progress_pct ? ` (${tribe.tribe_v2_progress_pct}%)` : ''}`,
    });
  } else {
    synthetic.push({ type: 'gpu', message: 'GPU: waiting for pipeline handoff' });
  }
  if (tribe?.error_msg) {
    synthetic.push({ type: 'error', message: `GPU error: ${tribe.error_msg.split('\n')[0].slice(0, 180)}` });
  }

  const normalizeType = (raw) => {
    const t = String(raw || '').trim().toLowerCase();
    if (!t) return 'event';
    if (t.includes('drop')) return 'drop';
    if (t.includes('peak')) return 'peak';
    if (t.includes('hook')) return 'peak';
    if (t.includes('error') || t.includes('fail')) return 'error';
    return 'event';
  };

  const prettifyType = (raw) => {
    const t = String(raw || '').trim();
    if (!t) return 'Signal event';
    return t
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const eventLines = (events || []).map((ev) => {
    const time = Number.isFinite(ev.time) ? ev.time : (Number.isFinite(ev.start) ? ev.start : undefined);
    const text = String(ev.text || ev.label || ev.description || '').trim();
    const typeLabel = prettifyType(ev.type);
    const typeNorm = normalizeType(ev.type);
    let message = '';

    if (text) {
      message = `${typeLabel}: ${text.slice(0, 140)}`;
    } else if (ev.type) {
      message = `${typeLabel} detected`;
    }

    return { type: typeNorm, time, message };
  }).filter(ev => ev.message);

  const merged = [...synthetic, ...eventLines]
    .filter(x => x.message)
    // Keep same-message rows only if they happen at different times;
    // this preserves timeline context while removing pure duplicates.
    .filter((x, i, arr) => i === arr.findIndex(y => y.message === x.message && y.time === x.time));

  const logLines = merged.slice(-24);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [logLines.length, job?.current_step, tribe?.tribe_v2_progress_stage, tribe?.tribe_v2_progress_pct]);

  if (!logLines.length) return null;

  return (
    <div style={{ marginTop: 14 }}>
      <p style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim, letterSpacing: '0.1em', marginBottom: 8 }}>
        GPU LOGS — {label}
      </p>
      <div
        ref={feedRef}
        style={{
          maxHeight: 180, overflowY: 'auto',
          background: '#09090b', borderRadius: 8,
          padding: '10px 12px',
          display: 'flex', flexDirection: 'column', gap: 3,
        }}
      >
        {logLines.map((ev, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{
              flexShrink: 0, fontSize: 9,
              fontFamily: 'var(--font-mono)',
              color: '#4b5563', paddingTop: 1,
            }}>
              {ev.time !== undefined ? `${String(Math.floor(ev.time)).padStart(2, '0')}s` : 'live'}
            </span>
            <span style={{
              fontSize: 10, fontFamily: 'var(--font-mono)',
              color: ev.type === 'error' ? '#f87171'
                   : ev.type === 'peak' ? '#34d399'
                   : ev.type === 'drop' ? '#fb7185'
                   : ev.type === 'gpu' ? '#fbbf24'
                   : ev.type === 'pipeline' ? '#93c5fd'
                   : '#a1a1aa',
              lineHeight: 1.4,
            }}>
              {ev.message}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Notify-me block ───────────────────────────────────────────
function NotifyMeBlock({ battle, userEmail }) {
  const email = battle?.notify_email || userEmail;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      style={{
        marginTop: 24, padding: '16px 20px',
        background: T.bgSubtle, border: `1px solid ${T.border}`,
        borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Bell size={13} style={{ color: T.muted }} />
        <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: T.fg }}>
          Result notification enabled
        </p>
      </div>
      <p style={{ margin: 0, fontSize: 11, color: T.muted, lineHeight: 1.5 }}>
        GPU analysis takes 3–8 minutes. We'll automatically email
        {email ? ` ${email}` : ' your account email'} when results are ready.
      </p>
    </motion.div>
  );
}

function ProcessingView({ battle, userEmail }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 48 }}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: T.dim, letterSpacing: '0.14em', marginBottom: 10 }}>
          TEST IN PROGRESS
        </p>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: T.fg, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
          Analysing your ads
        </h2>
        <p style={{ fontSize: 13, color: T.muted, fontFamily: 'var(--font-mono)', margin: 0 }}>
          Brain-scan model running on GPU · results typically in 3–8 min
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        style={{ display: 'flex', gap: 16, marginTop: 32, alignItems: 'stretch' }}
      >
        <JobColumn label="A" filename={battle.filename_a} job={battle.job_a} tribe={battle.tribe_v2_a} events={battle.events_a} />
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, color: T.dim,
          fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
        }}>VS</div>
        <JobColumn label="B" filename={battle.filename_b} job={battle.job_b} tribe={battle.tribe_v2_b} events={battle.events_b} />
      </motion.div>

      <NotifyMeBlock battle={battle} userEmail={userEmail} />
    </div>
  );
}

// ── Phase 2: Results ─────────────────────────────────────────
const WINNER_CONFIG = {
  A:    { accent: '#18181B', bg: '#F9FAFB', border: '#E5E7EB', label: 'A WINS' },
  B:    { accent: '#D97706', bg: '#FFFBEB', border: '#FDE68A', label: 'B WINS' },
  TIED: { accent: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB', label: 'TOO CLOSE TO CALL' },
};

const CONFIDENCE_CONFIG = {
  high:   { label: 'HIGH CONFIDENCE', color: '#047857', bg: '#ECFDF3', border: '#A7F3D0' },
  medium: { label: 'MEDIUM CONFIDENCE', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  low:    { label: 'LOW CONFIDENCE', color: '#7C2D12', bg: '#FFF7ED', border: '#FDBA74' },
};

function SidePanel({ side, label, filename, jobId, currentTime, duration, onTimeChange, onDurationChange }) {
  const [jobResult, setJobResult] = useState(null);
  const isB = label === 'B';

  useEffect(() => {
    if (!jobId) return;
    axios.get(`${BACKEND_URL}/api/tribe/status/${jobId}`)
      .then(r => setJobResult(r.data))
      .catch(() => {});
  }, [jobId]);

  const insights = side?.tribe_v2_ready ? {
    attention:           side.attention,
    baseline_activation: side.baseline_activation,
    peak_second:         side.peak_second,
    drops:               side.drops,
    avg_activation:      side.avg_activation,
    n_timesteps:         side.n_timesteps,
  } : null;

  const frames       = jobResult?.tribe_v2_brain_frames || [];
  const videoUrl     = jobResult?.video_s3_url || null;
  const nTimesteps   = jobResult?.tribe_v2_n_timesteps || side?.n_timesteps || 0;
  const nVertices    = jobResult?.tribe_v2_n_vertices || 0;
  const regionScores = jobResult?.tribe_v2_region_scores || {};
  const vertexTimeline       = jobResult?.tribe_v2_vertex_timeline || null;
  const vertexTimelineLabels = jobResult?.tribe_v2_vertex_timeline_labels || null;
  const timeline     = jobResult?.tribe_v2_timeline || {};

  // Use actual video duration when known; fall back to timestep count as seconds
  const effectiveDuration = duration > 0 ? duration : nTimesteps;

  const hasBrainData = Object.keys(regionScores).length > 0 || (vertexTimeline?.length > 0);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 26, height: 26, borderRadius: 6,
          background: isB ? '#FEF3C7' : T.bgSubtle,
          border: `1px solid ${isB ? '#FDE68A' : T.border}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
          color: isB ? T.amberB : T.muted,
        }}>{label}</span>
        <p style={{
          fontSize: 12, color: T.muted, fontFamily: 'var(--font-mono)', margin: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220,
        }}>
          {filename?.split('/').pop() || filename || '—'}
        </p>
      </div>

      {/* Brain cinema — video is the clock master */}
      {frames.length > 0 && (
        <div style={{
          background: T.bgSubtle, border: `1px solid ${T.border}`,
          borderRadius: 12, overflow: 'hidden',
        }}>
          <TribeV2BrainCinema
            frames={frames}
            videoUrl={videoUrl}
            duration={effectiveDuration}
            nTimesteps={nTimesteps}
            currentTime={currentTime}
            onTimeChange={onTimeChange}
            onDurationChange={onDurationChange}
          />
        </div>
      )}

      {/* 3D Brain — lights up in sync with video position */}
      {hasBrainData && (
        <div style={{
          background: T.bgSubtle, border: `1px solid ${T.border}`,
          borderRadius: 12, overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 16px 0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <p style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim, letterSpacing: '0.1em', margin: 0 }}>
              NEURAL MAP — {label} · REAL-TIME SYNC
            </p>
            <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: T.subtle }}>
              DRAG TO ROTATE
            </span>
          </div>
          <BrainVisualization
            regionScores={regionScores}
            currentTime={currentTime}
            duration={effectiveDuration}
            timeline={timeline}
            vertexTimeline={vertexTimeline}
            vertexTimelineLabels={vertexTimelineLabels}
            nVertices={nVertices}
            nTimesteps={nTimesteps}
            paused={false}
          />
        </div>
      )}

      {/* Per-side curve — marker tracks video time */}
      {insights && (
        <div style={{
          background: T.bgSubtle, border: `1px solid ${T.border}`,
          borderRadius: 12, padding: '16px 20px',
        }}>
          <p style={{
            fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim,
            letterSpacing: '0.1em', marginBottom: 12,
          }}>
            NEURAL ACTIVATION — {label}
          </p>
          <EngagementCurve
            insights={insights}
            currentTime={currentTime}
            onTimeChange={onTimeChange}
            videoDuration={effectiveDuration}
          />
        </div>
      )}
    </div>
  );
}

function ShareBar({ battleId }) {
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading]   = useState(false);
  const [copied, setCopied]     = useState(false);
  const [open, setOpen]         = useState(false);

  const generate = async () => {
    if (shareUrl) { setOpen(true); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/tribe/battle/${battleId}/share-link`);
      setShareUrl(r.data.share_url);
      setOpen(true);
    } catch {
      // not ready yet — show full URL as fallback
      setShareUrl(window.location.href);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const downloadJSON = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/tribe/battle/${battleId}/report`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `tribe-battle-${battleId.slice(0, 8)}.json`;
      a.click();
    } catch { /* silent */ }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <button onClick={generate} disabled={loading} style={btnStyle(true)}>
        <Share2 size={13} />
        {loading ? 'Generating…' : 'Share report link'}
      </button>

      <button onClick={downloadJSON} style={btnStyle(false)}>
        <Download size={13} />
        Download data
      </button>

      {open && shareUrl && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: T.bgSubtle, border: `1px solid ${T.border}`,
          borderRadius: 8, padding: '5px 10px',
          maxWidth: 340,
        }}>
          <span style={{
            fontSize: 11, fontFamily: 'var(--font-mono)', color: T.muted,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>{shareUrl}</span>
          <button onClick={copy} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: T.muted, display: 'flex' }}>
            {copied ? <Check size={13} color="#059669" /> : <Copy size={13} />}
          </button>
        </div>
      )}
    </div>
  );
}

function btnStyle(primary) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 9,
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
    background: primary ? '#18181B' : T.bgSubtle,
    color: primary ? '#fff' : T.muted,
    border: `1px solid ${primary ? '#18181B' : T.border}`,
    transition: 'opacity 0.15s',
  };
}

// ── Provenance panel ─────────────────────────────────────────
function ProvenancePanel({ battle }) {
  const gpuA = battle.tribe_v2_a?.status;
  const gpuB = battle.tribe_v2_b?.status;
  const bothTriggered = ['submitted', 'completed', 'error'].includes(gpuA) && ['submitted', 'completed', 'error'].includes(gpuB);
  const allComplete = gpuA === 'completed' && gpuB === 'completed';
  const computeStatus = allComplete
    ? 'verified'
    : bothTriggered
      ? 'running'
      : 'pending';
  const computeLabel = allComplete
    ? 'T4 GPU complete · Verified run'
    : bothTriggered
      ? 'T4 GPU triggered · Processing'
      : 'T4 GPU pending';
  const evidenceItems = [
    { label: 'Model', value: 'Tribe V2 · 256-vertex cortical fMRI' },
    { label: 'Compute', value: computeLabel, status: computeStatus },
    { label: 'Dataset', value: 'Facebook Research fMRI brain scans' },
    { label: 'Output', value: 'Not LLM — actual neural signal processing' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      style={{
        marginTop: 24,
        padding: '20px',
        background: T.bg,
        borderRadius: 14,
        border: `1px solid ${T.border}`,
        boxShadow: '0 1px 2px rgba(9,9,11,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <p style={{
            fontSize: 10, fontFamily: 'var(--font-mono)', color: T.subtle,
            letterSpacing: '0.14em', margin: 0,
          }}>
            EVIDENCE TRAIL
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: T.fg }}>
            Verifiable proof this run used Tribe V2 compute
          </p>
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999,
          background: computeStatus === 'verified' ? '#ECFDF3' : computeStatus === 'running' ? '#FFFBEB' : T.bgSubtle,
          border: `1px solid ${computeStatus === 'verified' ? '#A7F3D0' : computeStatus === 'running' ? '#FDE68A' : T.border}`,
          color: computeStatus === 'verified' ? '#047857' : computeStatus === 'running' ? '#B45309' : T.muted,
          fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.08em',
          whiteSpace: 'nowrap',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: computeStatus === 'verified' ? '#10B981' : computeStatus === 'running' ? '#F59E0B' : '#A1A1AA',
          }} />
          {computeStatus === 'verified' ? 'VERIFIED' : computeStatus === 'running' ? 'RUNNING' : 'PENDING'}
        </span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: 10,
      }}>
        {evidenceItems.map(item => (
          <div key={item.label} style={{
            padding: '11px 12px', borderRadius: 10,
            background: T.bgSubtle, border: `1px solid ${T.border}`,
          }}>
            <span style={{ fontSize: 9, color: T.subtle, fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', display: 'block', marginBottom: 5 }}>
              {item.label.toUpperCase()}
            </span>
            <span style={{
              fontSize: 12,
              color: item.label === 'Compute'
                ? (computeStatus === 'verified' ? '#047857' : computeStatus === 'running' ? '#B45309' : T.muted)
                : T.fg,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1.5,
            }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ── Slack icon (inline SVG) ───────────────────────────────────
function SlackIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
    </svg>
  );
}

// ── Slack CTA ─────────────────────────────────────────────────
function SlackCTA() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.5 }}
      style={{
        marginTop: 32, padding: '20px 24px',
        background: '#F8F7FF',
        border: '1px solid #C4B5FD',
        borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#4A154B', color: '#fff',
            borderRadius: 4, padding: '2px 7px',
            fontSize: 9, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', fontWeight: 700,
          }}>
            <SlackIcon size={9} /> #the-ad-gene
          </span>
          <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: '#7C3AED', letterSpacing: '0.08em' }}>SLACK CHANNEL</span>
        </div>
        <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#1E1024' }}>
          Where the best media buyers quietly compare notes.
        </p>
        <p style={{ margin: 0, fontSize: 12, color: '#71717A', lineHeight: 1.55 }}>
          Real winning-ad teardowns. Benchmark drops. The kind of intel that doesn't end up in newsletters.
        </p>
      </div>
      <a
        href="https://join.slack.com/t/theadgene/shared_invite/zt-3wd4nva82-ow~P0FJd7TqbLD9aIAvZlQ"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 22px', borderRadius: 8,
          background: '#4A154B', color: '#fff',
          fontSize: 13, fontWeight: 600, textDecoration: 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
          transition: 'opacity 0.15s',
          boxShadow: '0 2px 8px rgba(74,21,75,0.25)',
        }}
        onMouseEnter={e => (e.currentTarget.style.opacity = '0.88')}
        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
      >
        <SlackIcon size={15} />
        Join Community
      </a>
    </motion.div>
  );
}

function ResultsView({ battle }) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const winner = battle.winner || 'TIED';
  const cfg    = WINNER_CONFIG[winner] || WINNER_CONFIG['TIED'];
  const confidenceKey = battle.decision_confidence || 'low';
  const confidenceCfg = CONFIDENCE_CONFIG[confidenceKey] || CONFIDENCE_CONFIG.low;
  const labelA = battle.label_a || 'Creative A';
  const labelB = battle.label_b || 'Creative B';
  const winnerLabel = winner === 'A' ? labelA : winner === 'B' ? labelB : null;
  const recommendation = battle.recommendation_strength || null;
  const recommendationText =
    recommendation === 'ship_now'
      ? 'Action: ship this variant now.'
      : recommendation === 'ship_with_monitoring'
        ? 'Action: ship with monitoring on first spend window.'
        : recommendation === 'validate_with_one_more_test'
          ? 'Action: run one more variant to validate before scaling.'
          : null;

  const normalizedTimeline = useMemo(() => {
    const raw = battle.timeline || [];
    if (!raw.length || !duration) return raw;

    const maxT = Math.max(...raw.map(p => Number(p?.t || 0)));
    // If timeline already looks like real seconds, leave unchanged.
    if (maxT <= duration * 1.15) return raw;

    // Re-map timeline points from sample index domain to actual video seconds.
    const n = Math.max(1, raw.length - 1);
    return raw.map((p, idx) => ({
      ...p,
      t: (idx / n) * duration,
    }));
  }, [battle.timeline, duration]);

  return (
    <div>
      {/* Verdict hero */}
      <motion.div
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          background: cfg.bg, border: `1px solid ${cfg.border}`,
          borderRadius: 16, padding: '32px 36px',
          textAlign: 'center', marginBottom: 28,
        }}
      >
        <p style={{
          fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.18em',
          color: T.muted, marginBottom: 10,
        }}>
          VERDICT
        </p>
        <h1 style={{
          fontSize: winner === 'TIED' ? 36 : 54,
          fontWeight: 800, letterSpacing: '-0.04em',
          color: cfg.accent, margin: '0 0 12px', lineHeight: 1,
        }}>
          {winner === 'TIED' ? 'TOO CLOSE TO CALL' : (winnerLabel || winner)}
        </h1>
        {winner !== 'TIED' && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '5px 10px', borderRadius: 999,
              border: `1px solid ${confidenceCfg.border}`,
              background: confidenceCfg.bg, color: confidenceCfg.color,
              fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em',
            }}>
              {confidenceCfg.label}
            </span>
            {typeof battle.decision_margin_pct === 'number' && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 11, color: T.muted,
              }}>
                EDGE: {battle.decision_margin_pct}%
              </span>
            )}
          </div>
        )}
        {battle.winner_reason && (
          <p style={{
            fontSize: 13, color: T.muted,
            fontFamily: 'var(--font-mono)', margin: 0,
            maxWidth: 500, marginLeft: 'auto', marginRight: 'auto',
            lineHeight: 1.65,
          }}>
            {battle.winner_reason}
          </p>
        )}
        {winner !== 'TIED' && recommendationText && (
          <p style={{
            margin: '10px 0 0',
            fontSize: 12, color: T.dim, fontFamily: 'var(--font-mono)',
          }}>
            {recommendationText}
          </p>
        )}
      </motion.div>

      {/* Share / export bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <p style={{ margin: 0, fontSize: 12, color: T.muted, fontFamily: 'var(--font-mono)' }}>
          Share this result with your team and keep a permanent proof link.
        </p>
        <ShareBar battleId={battle.battle_id || battle.id} />
      </div>

      {/* Dual curve */}
      {normalizedTimeline?.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            background: T.bgSubtle, border: `1px solid ${T.border}`,
            borderRadius: 16, padding: '24px 28px', marginBottom: 20,
          }}
        >
          <p style={{
            fontSize: 9, fontFamily: 'var(--font-mono)', color: T.dim,
            letterSpacing: '0.12em', marginBottom: 20,
          }}>
            NEURAL ENGAGEMENT COMPARISON
          </p>
          <DualEngagementCurve
              timeline={normalizedTimeline}
            sideA={{ ...battle.side_a, name: labelA }}
            sideB={{ ...battle.side_b, name: labelB }}
            currentTime={currentTime}
            onTimeChange={setCurrentTime}
          />
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Side panels — responsive on narrower screens */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 420px', minWidth: 0 }}>
            <SidePanel
              side={battle.side_a} label="A"
              filename={battle.filename_a} jobId={battle.job_id_a}
              currentTime={currentTime} duration={duration}
              onTimeChange={setCurrentTime} onDurationChange={setDuration}
            />
          </div>
          <div style={{ flex: '1 1 420px', minWidth: 0 }}>
            <SidePanel
              side={battle.side_b} label="B"
              filename={battle.filename_b} jobId={battle.job_id_b}
              currentTime={currentTime} duration={duration}
              onTimeChange={setCurrentTime} onDurationChange={setDuration}
            />
          </div>
        </div>

        {/* Evidence trail — verifiable proof of real neural scan */}
        <ProvenancePanel battle={battle} />
      </motion.div>

      {/* Community CTA */}
      <SlackCTA />
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────
export default function Battle() {
  const { battleId } = useParams();
  const navigate     = useNavigate();
  const [battle, setBattle] = useState(null);
  const [error,  setError]  = useState('');
  const pollRef = useRef(null);
  const autoNotifyRef = useRef(false);
  const { refreshUser, user } = useAuthStore();

  const fetchBattle = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/tribe/battle/${battleId}`);
      setBattle(r.data);
      const d = r.data;
      // Backward compatibility: ensure older battles also have auto-notify enabled.
      if (!autoNotifyRef.current && d?.battle_id && !d?.notify_email) {
        autoNotifyRef.current = true;
        axios.post(`${BACKEND_URL}/api/tribe/battle/${d.battle_id}/notify`).catch(() => {});
      }
      // Stop polling when terminal state reached
      const gpuA = d.tribe_v2_a?.status;
      const gpuB = d.tribe_v2_b?.status;
      const bothGpuDone = ['completed', 'error'].includes(gpuA) && ['completed', 'error'].includes(gpuB);
      if (['ready', 'error'].includes(d.battle_status) || bothGpuDone) {
        clearInterval(pollRef.current);
        // Refresh user so navbar credit count reflects any refund (on error) or deduction (on success)
        refreshUser().catch(() => {});
      }
    } catch {
      setError('Failed to load battle.');
      clearInterval(pollRef.current);
    }
  }, [battleId, refreshUser]);

  useEffect(() => {
    fetchBattle();
    pollRef.current = setInterval(fetchBattle, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchBattle]);

  const isReady = battle?.battle_status === 'ready';

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      <Nav navigate={navigate} />

      <div style={{ maxWidth: 980, margin: '0 auto', padding: '92px 24px 80px' }}>

        {/* Initial load */}
        {!battle && !error && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: '60vh', gap: 10,
            color: T.muted, fontFamily: 'var(--font-mono)', fontSize: 12,
          }}>
            <Spinner color={T.muted} /> Loading battle…
          </div>
        )}

        {error && (
          <div style={{
            textAlign: 'center', paddingTop: 120,
            color: T.red, fontFamily: 'var(--font-mono)', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {battle && battle.battle_status === 'error' && (
            <div style={{
              textAlign: 'center', paddingTop: 80,
              fontFamily: 'var(--font-mono)', fontSize: 12, color: T.red, lineHeight: 1.7,
            }}>
              <p>Analysis failed.</p>
              <p style={{ color: T.muted, marginTop: 8 }}>
                Please retry your test. If this keeps happening, contact support.
              </p>
            </div>
          )}

          {battle && !isReady && battle.battle_status !== 'error' && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            >
              <ProcessingView battle={battle} userEmail={user?.email} />
            </motion.div>
          )}

          {battle && isReady && (
            <motion.div
              key="results"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <ResultsView battle={battle} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
